// SPDX-FileCopyrightText: 2026 Povl Filip Sonne-Frederiksen
//
// SPDX-License-Identifier: GPL-3.0-or-later

import { api } from '../../api/client';
import type { FrameVisibilityList, VisibleFrame } from '../../api/types';
import { bestFrame, formatScore, rankLabel } from '../../data/sourceImage';
import { Button } from '../Button';
import { IconButton, XIcon } from '../IconButton';
import styles from './SourceImages.module.css';

/** Thumbnail edge: 2× the grid cell, for HiDPI. */
const THUMB_MAX_SIZE = 240;

export interface SourceImagesProps {
  /** The ranked frames from `/frames/visibility`; null while the request runs. */
  visibility: FrameVisibilityList | null;
  error: Error | null;
  onClose: () => void;
  /** Open a frame; the host seeds Segmentering with the pixel (`u`, `v`) the point projects to. */
  onOpenFrame: (frame: VisibleFrame) => void;
}

/**
 * Kildebilleder: the sensor frames that see the point picked with the
 * Ground's Vælg punkt tool, most central first (#454; the old viewport's
 * SourceImagePanel in the Bygning shell's panel idiom). A frame opens in
 * Segmentering with the point as a prompt.
 */
export function SourceImages({ visibility, error, onClose, onOpenFrame }: SourceImagesProps) {
  const best = visibility ? bestFrame(visibility.frames) : null;
  const total = visibility?.total ?? 0;
  return (
    <section className={styles.panel} aria-label="Kildebilleder">
      <header className={styles.head}>
        <h2 className={styles.title}>Kildebilleder</h2>
        {visibility && (
          <span className={styles.count}>
            {total.toLocaleString('da-DK')} {total === 1 ? 'billede' : 'billeder'}
          </span>
        )}
        <IconButton variant="ghost" label="Luk kildebilleder" onClick={onClose}>
          <XIcon />
        </IconButton>
      </header>
      <div className={styles.body}>
        {error && (
          <p className={styles.error} role="alert">
            Billederne kunne ikke hentes: {error.message}
          </p>
        )}
        {!error && visibility === null && <p className={styles.note}>Finder billeder…</p>}
        {!error && visibility && visibility.frames.length === 0 && (
          <p className={styles.note}>Intet billede ser dette punkt.</p>
        )}
        {!error && visibility && visibility.frames.length > 0 && (
          <>
            {best && (
              <Button variant="primary" size="sm" block onClick={() => onOpenFrame(best)}>
                Del op i bedste billede
              </Button>
            )}
            <div className={styles.grid}>
              {visibility.frames.map((frame, i) => (
                <button
                  key={frame.frame_id}
                  type="button"
                  className={styles.thumb}
                  onClick={() => onOpenFrame(frame)}
                  title={`Billede ${frame.frame_id} · ${formatScore(frame.score)} centralt · ${frame.depth.toLocaleString('da-DK', { maximumFractionDigits: 1 })} m væk — åbn i Segmentering`}
                >
                  <img
                    src={api.frameImageUrl(frame.frame_id, 'color', { maxSize: THUMB_MAX_SIZE })}
                    alt={`Billede ${frame.frame_id}`}
                    className={styles.img}
                    loading="lazy"
                  />
                  <span className={`${styles.badge}${i === 0 ? ` ${styles.badgeBest}` : ''}`}>{rankLabel(i)}</span>
                  <span className={styles.score}>{formatScore(frame.score)}</span>
                </button>
              ))}
            </div>
          </>
        )}
      </div>
    </section>
  );
}
